import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/Users/754368/Workspace/fp-tutorials/fp/src/components/PageLayout/index.js";
import Link from '../../components/Link';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p><strong parentName="p">{`Functional programming in JavaScript is easy.`}</strong></p>
    <p>{`Easier, in fact, than OOP in JavaScript.`}</p>
    <p>{`If you've been looking at academic discourses about functional programming with terms such as `}<em parentName="p">{`referential transparency`}</em>{`, `}<em parentName="p">{`lambda calculus`}</em>{`, and the dreaded `}<em parentName="p">{`monad`}</em>{`, then it's no wonder you're confused. But you don't need to understand any of that terminology (or even the theory) to `}<em parentName="p">{`FP`}</em>{`.`}</p>
    <p>{`Here we present the fundamental `}<strong parentName="p">{`techniques`}</strong>{` for functional programming without all the painful academic theory and terminology. You, too, can FP.`}</p>
    <h2>{`How to FP`}</h2>
    <ul>
  <li>
    <Link to='/inductive/reusability/' aria-label='Reusability' mdxType="Link">Functions are the basic unit of reusability</Link>
    <ul>
      <li><Link to='/inductive/reusability/#purity-rules' mdxType="Link">Given the same arguments, a function must return the same result</Link></li>
      <li><Link to='/inductive/reusability/#real-functions-have-parameters' mdxType="Link">Functions take at least one parameter</Link></li>
      <li><Link to='/inductive/reusability/#real-functions-return-a-value' mdxType="Link">Functions return exactly one result</Link></li>
      <li><Link to='/inductive/reusability/#join-the-higher-order' mdxType="Link">Functions can be passed as arguments to other functions</Link></li>
      <li><Link to='/inductive/reusability/#who-wants-curry' mdxType="Link">Functions can be returned from other functions</Link></li>
      <li><Link to='/inductive/reusability/#we-can-has-recursion' mdxType="Link">Functions can call functions, <em>including themselves</em></Link></li>
      <li><Link to='/inductive/reusability/#say-no-to-side-effects' mdxType="Link">Don't be reaching outside the function to mess with shit</Link></li>
      <li><Link to='/inductive/reusability/#no-mutating-variables' mdxType="Link">Make copies when passing by reference</Link></li>
      <li><Link to='/inductive/reusability/#closures-are-cool' mdxType="Link">Functions can remember things for you</Link></li>
    </ul>
  </li>
  <li>
    <Link to='/inductive/immutability/' aria-label='Immutability' mdxType="Link">Variables are &ldquo;set and forget&rdquo;</Link>
    <ul>
      <li><Link to='/inductive/immutability/#use-const-exclusively' mdxType="Link">Use <code>const</code> exclusively!</Link></li>
      <li><Link to='/inductive/immutability/#assign-upon-declaration' mdxType="Link">Assign variables when they are declared</Link></li>
      <li><Link to='/inductive/immutability/#never-mutate' mdxType="Link">Once a variable has been assigned, don't be changing it</Link></li>
      <li><Link to='/inductive/immutability/#never-destroy-state' mdxType="Link">Don't erase state: create new state instead!</Link></li>
      <li><Link to='/inductive/immutability/#use-destructuring-to-make-copies' mdxType="Link">Use destructuring and the rest/spread operators to make copies</Link></li>
    </ul>
  </li>
  <li>
    <Link to='/inductive/iteration/' aria-label='Iteration' mdxType="Link">Loops are so last millennium!</Link>
    <ul>
      <li><Link to='/inductive/iteration/#recusrive-functions' mdxType="Link">Functions can call themselves! No, really!</Link></li>
      <li><Link to='/inductive/iteration/#map' mdxType="Link">Use <code>map</code> to modify each value in a list</Link></li>
      <li><Link to='/inductive/iteration/#reduce' mdxType="Link">Use <code>reduce</code> to combine elements in a list</Link></li>
      <li><Link to='/inductive/iteration/#reduce-again' mdxType="Link">More fun with <code>reduce</code></Link></li>
      <li><Link to='/inductive/iteration/#filter' mdxType="Link">Use <code>filter</code> to choose particular values from a list</Link></li>
      <li><Link to='/inductive/iteration/#adjust' mdxType="Link">Use <code>adjust</code> to update a single value in a list</Link></li>
      <li><Link to='/inductive/iteration/#append-insert-prepend' mdxType="Link">Use <code>append</code>, <code>insert</code>, or <code>prepend</code> to add an item to a list</Link></li>
      <li><Link to='/inductive/iteration/#remove' mdxType="Link">Use <code>remove</code> to remove an item from a list</Link></li>
      <li><Link to='/inductive/iteration/#concat' mdxType="Link">Use <code>concat</code> or <code>zip</code> to combine two lists</Link></li>
      <li><Link to='/inductive/iteration/#times' mdxType="Link">Use <code>times</code> to create a new list</Link></li>
      <li><Link to='/inductive/iteration/#head-tail' mdxType="Link">Use <code>head</code> and <code>tail</code> to destructure a list</Link></li>
      <li><Link to='/inductive/iteration/#all' mdxType="Link">Use <code>all</code>, <code>any</code>, or <code>none</code> to test a list</Link></li>
      <li><Link to='/inductive/iteration/#flatten' mdxType="Link">Use <code>flatten</code> to flatten nested lists into a single list</Link></li>
      <li><Link to='/inductive/iteration/#avoid-foreach' mdxType="Link">Avoid <code>forEach</code>! Avoid <code>forEach</code>! Avoid <code>forEach</code>!</Link></li>
    </ul>
  </li>
  <li>
    <Link to='/inductive/composition/' aria-label='Composition' mdxType="Link">We can has pipelines!</Link>
    <ul>
      <li><Link to='/inductive/composition/#curry' mdxType="Link">Use <code>curry</code> to make functions that make functions</Link></li>
      <li><Link to='/inductive/composition/#pipe' mdxType="Link">Use <code>pipe</code> or <code>compose</code> to combine functions into pipelines</Link></li>
      <li><Link to='/inductive/composition/#state-state-state' mdxType="Link">Think in terms of state!</Link></li>
    </ul>
  </li>
    </ul>
    <small>Errors, bugs, suggestions, questions? Contact <a href='mailto:charles_munat@bnz.co.nz'>Charles Munat</a></small>.

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      